import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Typography,
  TablePagination,
  TextField,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import SearchableSelect from '../../components/SearchableSelect';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import mcpService, { IMcpPagedResult, Mcp, MCP_TIPOS } from '../../services/mcpService';
import grupoMcpService, { GrupoMcp } from '../../services/grupoMcpService';
import carteraService from '../../services/carteraService';
import {
  loadTesoreriaFiltros,
  storedNumber,
  storedString,
  TESORERIA_FILTROS_KEYS,
  usePersistTesoreriaFiltros,
} from '../../utils/tesoreriaFiltrosStorage';

interface McpsFiltrosStored {
  searchTerm?: string;
  rowsPerPage?: number;
  tipoFiltro?: string;
  grupoFiltro?: string;
}

const Mcps: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const loadStored = () => loadTesoreriaFiltros<McpsFiltrosStored>(TESORERIA_FILTROS_KEYS.mcps);

  const [data, setData] = useState<IMcpPagedResult>({
    items: [],
    totalCount: 0,
    pageNumber: 1,
    pageSize: 10,
    totalPages: 0,
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => storedNumber(loadStored(), 'rowsPerPage', 10));
  const [searchTerm, setSearchTerm] = useState(() => storedString(loadStored(), 'searchTerm', ''));
  const [tipoFiltro, setTipoFiltro] = useState(() => storedString(loadStored(), 'tipoFiltro', ''));
  const [grupoFiltro, setGrupoFiltro] = useState(() => storedString(loadStored(), 'grupoFiltro', ''));
  const [grupoOpciones, setGrupoOpciones] = useState<GrupoMcp[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loading, setLoading] = useState(false);
  const [grupoMap, setGrupoMap] = useState<Record<number, string>>({});
  const [carteraMap, setCarteraMap] = useState<Record<number, string>>({});

  const fetchLookups = async (items: Mcp[]) => {
    const grupoIds = Array.from(new Set(items.map((r) => r.grupo).filter((g): g is number => g != null)));
    const carteraIds = Array.from(new Set(items.map((r) => r.cartera).filter((c): c is number => c != null)));

    const [grupos, carteras] = await Promise.all([
      Promise.allSettled(grupoIds.map((id) => grupoMcpService.getGrupoMcp(id).then((g) => ({ id, label: g.descripcion?.trim() || String(id) })))),
      Promise.allSettled(carteraIds.map((id) => carteraService.getCartera(id).then((c) => ({ id, label: c.nombreBanco?.trim() || String(id) })))),
    ]);

    const gMap: Record<number, string> = {};
    for (const r of grupos) { if (r.status === 'fulfilled') gMap[r.value.id] = r.value.label; }

    const cMap: Record<number, string> = {};
    for (const r of carteras) { if (r.status === 'fulfilled') cMap[r.value.id] = r.value.label; }

    setGrupoMap(gMap);
    setCarteraMap(cMap);
  };

  const fetchMcps = async (
    pageNumber: number,
    pageSize: number,
    search?: string,
    tipo?: string,
    grupoId?: number,
  ) => {
    try {
      setLoading(true);
      const response = await mcpService.getMcps({
        pageNumber,
        pageSize,
        searchTerm: (search ?? '').trim() || undefined,
        tipo: tipo || undefined,
        grupo: grupoId,
      });
      setData(response);
      fetchLookups(response.items);
    } catch (error: any) {
      console.error('Error al cargar medios MCP:', error);
      enqueueSnackbar('Error al cargar medios MCP', { variant: 'error' });
      setData({ items: [], totalCount: 0, pageNumber: 1, pageSize, totalPages: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const all: GrupoMcp[] = [];
        let pageNum = 1;
        let totalPages = 1;
        do {
          const res = await grupoMcpService.getGruposMcp({ pageNumber: pageNum, pageSize: 50 });
          if (cancelled) return;
          all.push(...res.items);
          totalPages = res.totalPages;
          pageNum += 1;
        } while (pageNum <= totalPages);
        all.sort((a, b) => (a.grupoMcp ?? 0) - (b.grupoMcp ?? 0));
        if (!cancelled) setGrupoOpciones(all);
      } catch (error) {
        console.error('Error al cargar grupos MCP:', error);
        if (!cancelled) enqueueSnackbar('Error al cargar grupos MCP', { variant: 'error' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enqueueSnackbar]);

  const grupoFiltroNum = (() => {
    if (!grupoFiltro.trim()) return undefined;
    const n = Number(grupoFiltro);
    if (!Number.isInteger(n) || n < 0 || n > 255) return undefined;
    return n;
  })();

  usePersistTesoreriaFiltros(
    TESORERIA_FILTROS_KEYS.mcps,
    { searchTerm, rowsPerPage, tipoFiltro, grupoFiltro },
    [searchTerm, rowsPerPage, tipoFiltro, grupoFiltro]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchMcps(page + 1, rowsPerPage, searchTerm, tipoFiltro, grupoFiltroNum);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [page, rowsPerPage, searchTerm, tipoFiltro, grupoFiltro]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const tipoFilterOptions = useMemo(
    () => MCP_TIPOS.map((t) => ({ value: t.value, label: t.label })),
    [],
  );

  const grupoFilterOptions = useMemo(
    () =>
      grupoOpciones.map((g) => ({
        value: String(g.grupoMcp),
        label: (g.descripcion ?? '').trim() || String(g.grupoMcp),
      })),
    [grupoOpciones],
  );

  const goEdit = (mcp: string, tipo: string) => {
    navigate(`/tesoreria/medios-mcp/${encodeURIComponent(mcp)}/${encodeURIComponent(tipo)}`);
  };

  const handleDelete = async (mcp: string, tipo: string) => {
    try {
      const check = await mcpService.puedeEliminar(mcp, tipo);
      if (!check.canDelete) {
        enqueueSnackbar(check.message || check.motivos?.[0] || 'No se puede eliminar el medio de pago.', { variant: 'error' });
        return;
      }
      if (!window.confirm(`¿Eliminar el medio MCP "${mcp}" (${tipo})?`)) return;
      await mcpService.deleteMcp(mcp, tipo);
      enqueueSnackbar('Medio MCP eliminado', { variant: 'success' });
      fetchMcps(page + 1, rowsPerPage, searchTerm, tipoFiltro, grupoFiltroNum);
    } catch (error: unknown) {
      console.error('Error al eliminar MCP:', error);
      const axiosError = error as { response?: { data?: { message?: string } } };
      enqueueSnackbar(axiosError?.response?.data?.message || 'Error al eliminar', { variant: 'error' });
    }
  };

  const tipoLabel = (t: string) => MCP_TIPOS.find((x) => x.value === t)?.label || t;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Medios MCP
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/tesoreria/medios-mcp/nuevo')}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 500,
                boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                '&:hover': { boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)' },
              }}
            >
              Nuevo medio
            </Button>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar por código, tipo o descripción..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#fafafa',
                  '&:hover': { bgcolor: '#f5f5f5' },
                  '&.Mui-focused': { bgcolor: '#ffffff' },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
              }}
            />
            <SearchableSelect
              label="Tipo"
              value={tipoFiltro || null}
              onChange={(val) => {
                setTipoFiltro(val ?? '');
                setPage(0);
              }}
              options={tipoFilterOptions}
              allowEmpty
              emptyOptionLabel="Todos"
              sx={{ minWidth: { xs: '100%', sm: 200 }, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <SearchableSelect
              label="Grupo"
              value={grupoFiltro || null}
              onChange={(val) => {
                setGrupoFiltro(val ?? '');
                setPage(0);
              }}
              options={grupoFilterOptions}
              allowEmpty
              emptyOptionLabel="Todos"
              sx={{ minWidth: { xs: '100%', sm: 220 }, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Stack>

          {isMobile ? (
            <Stack spacing={2}>
              {data.items.map((row) => (
                <Card
                  key={`${row.mcp}-${row.tipo}`}
                  sx={{
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.1)' },
                  }}
                >
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {row.mcp?.trim() || '-'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {tipoLabel(row.tipo)}
                          </Typography>
                          <Typography variant="body2">{row.descripcion?.trim() || '-'}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Grupo: {row.grupo != null ? (grupoMap[row.grupo] ?? row.grupo) : '-'} · Cartera: {row.cartera != null ? (carteraMap[row.cartera] ?? row.cartera) : '-'}
                          </Typography>
                        </Box>
                        <Stack direction="row">
                          <Tooltip title="Editar">
                            <IconButton color="primary" onClick={() => goEdit(row.mcp, row.tipo)} size="small">
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar">
                            <IconButton color="error" onClick={() => handleDelete(row.mcp, row.tipo)} size="small">
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
              {data.items.length === 0 && (
                <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                  No hay registros
                </Typography>
              )}
            </Stack>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>MCP</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Grupo
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Cartera
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.items.map((row) => (
                    <TableRow key={`${row.mcp}-${row.tipo}`} hover>
                      <TableCell>{row.mcp?.trim() || '-'}</TableCell>
                      <TableCell>{tipoLabel(row.tipo)}</TableCell>
                      <TableCell>{row.descripcion?.trim() || '-'}</TableCell>
                      <TableCell align="right">{row.grupo != null ? (grupoMap[row.grupo] ?? row.grupo) : '-'}</TableCell>
                      <TableCell align="right">{row.cartera != null ? (carteraMap[row.cartera] ?? row.cartera) : '-'}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Editar">
                          <IconButton color="primary" onClick={() => goEdit(row.mcp, row.tipo)} size="small">
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton color="error" onClick={() => handleDelete(row.mcp, row.tipo)} size="small">
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <TablePagination
            component="div"
            count={data.totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="Filas"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default Mcps;
